// src/views/kiddo-games/pairing/Picture.tsx
// Kopia src/components/Pairing/Picture.tsx — tylko dla Sparuj Easy
import React, {
  FC, useEffect, useRef, memo,
} from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import useKeepOnBoard from '../../../hooks/useKeepOnBoard';
import classes from './Picture.module.less';
import boardClasses from './KiddoPairingView.module.less';
import getCounterStripBottom from './counterStrip';

gsap.registerPlugin(Draggable);

type PictureProps = {
  id: string | number;
  link: string;
  translation: string;
  isMatched: boolean;
  isPaired: boolean;
  isMatching: boolean;
  // Kliknięcie albo tapnięcie obrazka (bez przesunięcia go) — dostaje jego polskie znaczenie
  onTap: (translation: string) => void;
};

export const Picture: FC<PictureProps> = memo(({
  id,
  link,
  translation,
  isMatched,
  isPaired,
  isMatching,
  onTap,
}: PictureProps) => {
  const pictureId = `picture-${id}`;
  const draggableRef = useRef<Draggable[] | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  // Draggable powstaje raz, a efekt niżej nie może dostać nowych zależności (ich zmiana zabiłaby Draggable),
  // więc onTap i znaczenie bierze przez ref
  const tapRef = useRef(() => onTap(translation));
  tapRef.current = () => onTap(translation);

  // Po zmianie rozmiaru planszy obrazek przesuwa się proporcjonalnie i nie wypada za krawędź ani na pas z licznikiem plansz
  useKeepOnBoard(elementRef, getCounterStripBottom);

  useEffect(() => {
    const pictureElement = elementRef.current;
    if (!pictureElement || isPaired || initializedRef.current) {
      return undefined;
    }

    setTimeout(() => {
      const boardElement = document.querySelector(`.${boardClasses.boardForPics}`);
      if (!boardElement) return;

      const boardRect = boardElement.getBoundingClientRect();
      const maxX = boardRect.width - pictureElement.offsetWidth - 20;
      const maxY = boardRect.height - pictureElement.offsetHeight - 20;
      // Pod pasem z licznikiem plansz, żeby obrazek go nie zasłonił
      const minY = Math.max(20, getCounterStripBottom());

      const randomX = 20 + Math.random() * (maxX - 20);
      const randomY = minY + Math.random() * (maxY - minY);

      const centerX = boardRect.width / 2 - pictureElement.offsetWidth / 2;
      const centerY = boardRect.height / 2 - pictureElement.offsetHeight / 2;

      // 🎯 Ustaw style PRZED dodaniem klasy
      pictureElement.style.position = 'absolute';
      pictureElement.style.left = `${centerX}px`;
      pictureElement.style.top = `${centerY}px`;
      pictureElement.style.transform = 'scale(0.3) rotate(0deg)';

      // 🎯 Dodaj klasę, żeby CSS przestał ukrywać
      pictureElement.classList.add(classes.initialized);

      // 🎯 ANIMACJA ROZRZUCENIA
      gsap.to(pictureElement, {
        left: randomX,
        top: randomY,
        opacity: 1,
        scale: 1,
        rotation: () => gsap.utils.random(-5, 5),
        duration: 0.8,
        delay: Math.random() * 0.4,
        ease: 'back.out(1.4)',
      });

      gsap.delayedCall(1.2, () => {
        draggableRef.current = Draggable.create(`#${pictureId}`, {
          type: 'x,y',
          edgeResistance: 0.65,
          // Woła je tylko kliknięcie bez przesunięcia — przytrzymanie i przeciąganie nie czyta
          onClick: () => tapRef.current(),
        });
      });

      initializedRef.current = true;
    }, 100);

    return () => {
      if (draggableRef.current) {
        draggableRef.current.forEach((instance) => instance.kill());
        draggableRef.current = null;
      }
    };
  }, [pictureId, isPaired, id]);

  // ✅ ZIELONY BORDER – trafiony obrazek stoi w miejscu, dopóki lektor czyta
  useEffect(() => {
    if (!isMatched || !elementRef.current) {
      return undefined;
    }

    const element = elementRef.current;

    element.style.border = '5px solid green';
    element.style.boxShadow = '0 0 4px rgba(0, 255, 0, 0.8)';

    if (draggableRef.current) {
      draggableRef.current.forEach((instance) => instance.disable());
    }

    return undefined;
  }, [isMatched]);

  // ✅ SHRINK
  useEffect(() => {
    if (!isMatching || !elementRef.current) {
      return undefined;
    }

    const element = elementRef.current;

    gsap.to(element, {
      scale: 0,
      rotation: 480,
      opacity: 0,
      duration: 0.5,
      ease: 'back.in(1.2)',
      onComplete: () => {
        element.style.boxShadow = '';
      },
    });

    return undefined;
  }, [isMatching]);

  if (isPaired) return null;

  return (
    <div
      ref={elementRef}
      id={pictureId}
      className={classes.picture}
    >
      <img alt={translation} src={link} />
    </div>
  );
});

export default Picture;
